import React, { useEffect, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import Swal from 'sweetalert2'
import { useCustomReducer } from '../../../../../base/customHooks'
import { BaseComponentWithContextProps, componentWithContext } from '../../../../../base/customHooks/componentHOC'
import IPButton from '../../../../../components/IPButton'
import IPCheckbox from '../../../../../components/IPCheckBox'
import IPInput from '../../../../../components/IPInput'
import IPFile from '../../../../../components/IPFile'
import IPSelectBox, { ISelectOption } from '../../../../../components/IPSelectBox/IPSelectBox'
import { excelToJSON } from '../../../../../base/utils/dataHelper'
import { GetVideoCategoryListApi } from '../services/getVideoCategoryListApi'
import { PostVideoLargeApi } from '../services/postVideoLargeApi'
import { IVideo } from '../models/responseModels'
import CampaignSelect from '../../../ProductOperation/CampaignBundleCrud/components/CampaignSelect'
import { DealerService } from '../../../ProductOperation/CampaignBundleCrud/services/DealerService'
import { DealerType } from '../../../ProductOperation/CampaignBundleCrud/models/CampaignBundleModel'
import { GetSubTypesApi } from '../../../components/services/getSubTypesApi'
import '../../../ProductOperation/CampaignBundleCrud/components/CampaignBundleList.scss'

enum CariListType {
  CariCode,
  CariType,
  CariAltType,
}

const toOptions = (arr?: string[]): ISelectOption[] => (arr ?? []).map(v => ({ value: v, label: v }))

interface IAddVideoState {
  categoryOptions: ISelectOption[]
  category?: ISelectOption
  title: string
  description: string
  sourceType: ISelectOption
  externalUrl: string
  file?: File
  fileName?: string
  thumbnail?: File
  orderNo: number
  active: boolean
  selectedDealerList?: CariListType
  dealerCodeList: ISelectOption[]
  dealerTypeList: ISelectOption[]
  dealerSubTypeList: ISelectOption[]
  dealerTypeOptions: ISelectOption[]
  dealerSubTypeOptions: ISelectOption[]
  dealerInclude: boolean
  dealerTypeInclude: boolean
  dealerSubTypeInclude: boolean
}

const sourceTypeOptions: ISelectOption[] = [
  { value: 0, label: 'Video Dosyası Yükle' },
  { value: 1, label: 'Video Linki (YouTube / Vimeo)' },
  { value: 2, label: 'SCORM Paketi (.zip)' },
]

// stored dealer codes are SAP codes (0000-prefixed); the UI works with the short code
const stripSap = (code: string) => code.replace(/^0000/, '')

function AddVideo(props: BaseComponentWithContextProps) {
  const routeLocation = useLocation()
  const navigate = useNavigate()
  const editData: IVideo | undefined = (routeLocation.state as any)?.data
  const isUpdate = (routeLocation.state as any)?.flag === 'update'

  const getInitialState = (): IAddVideoState => {
    return {
      categoryOptions: [],
      title: editData?.Title ?? '',
      description: editData?.Description ?? '',
      sourceType: sourceTypeOptions.find(x => x.value === (editData?.SourceType ?? 0)) as ISelectOption,
      externalUrl: editData?.ExternalUrl ?? '',
      orderNo: editData?.OrderNo ?? 0,
      active: editData?.Active ?? true,
      selectedDealerList: CariListType.CariCode,
      dealerCodeList: toOptions((editData?.DealerCodes ?? []).map(stripSap)),
      dealerTypeList: toOptions(editData?.DealerTypes),
      dealerSubTypeList: toOptions(editData?.DealerSubTypes),
      dealerTypeOptions: [],
      dealerSubTypeOptions: [],
      dealerInclude: editData?.DealerCodesInclude ?? true,
      dealerTypeInclude: editData?.DealerTypesInclude ?? true,
      dealerSubTypeInclude: editData?.DealerSubTypesInclude ?? true,
    }
  }

  const [state, setState] = useCustomReducer<IAddVideoState>(getInitialState())
  const [inputValue, setInputValue] = useState('')

  useEffect(() => {
    getCategories()
    getDealerTypes()
    getSubTypes()
  }, [])

  const getCategories = async () => {
    const api = new GetVideoCategoryListApi({})
    api.getVideoCategories().then(res => {
      const options = res.map(c => ({ value: c.Id, label: c.Name }))
      setState({
        categoryOptions: options,
        category: editData ? options.find(o => o.value === editData.VideoCategoryId) : undefined,
      })
    })
  }

  const getDealerTypes = async () => {
    try {
      const dealerService = new DealerService({})
      const res: any = await dealerService.getDealerTypes()
      if (res) {
        const dealerTypeOptions: ISelectOption[] = []
        res.forEach((item: DealerType) => {
          item && dealerTypeOptions.push({ value: item.MusteriGrubu, label: item.MusteriGrubuAciklama })
        })
        setState({ dealerTypeOptions })
      }
    } catch {
      /* optional */
    }
  }

  const getSubTypes = async () => {
    try {
      const api = new GetSubTypesApi({})
      const res = await api.getSubTypes()
      if (res) {
        const dealerSubTypeOptions: ISelectOption[] = []
        res.forEach((item: any) => {
          item && dealerSubTypeOptions.push({ value: item, label: item })
        })
        setState({ dealerSubTypeOptions })
      }
    } catch {
      /* optional */
    }
  }

  const enumFunction = (takenState: any, takenEnum: any) => (takenState === takenEnum ? 'active' : '')

  const setDealerCodes = (dealerCodes: any, optionArr: 'dealerTypeOptions' | 'dealerSubTypeOptions', fieldValue: string) => {
    if (fieldValue === 'XDealerCodeList') {
      const codeData = dealerCodes.map((item: any) => ({ label: String(item.DealerCode), value: String(item.DealerCode) }))
      const list: { label: string; value: string }[] = []
      codeData.forEach((codeItem: any) => {
        if (!state.dealerCodeList.some((item: any) => item.label === String(codeItem.label))) list.push(codeItem)
      })
      setState({
        dealerCodeList: [
          ...state.dealerCodeList,
          ...list.filter((item, index, self) => index === self.findIndex(i => i.label === item.label)),
        ],
      })
      return
    }

    const list: any[] = []
    const notFound: any[] = []
    dealerCodes.forEach((item: any) => {
      const match: any = state[optionArr]?.find((code: any) =>
        item.DealerCode < 6 ? code.value === item.DealerCode : code.value === '0000' + item.DealerCode
      )
      if (match === undefined) notFound.push(item.DealerCode)
      else list.push(match)
    })

    const apply = () => setState(fieldValue === 'XDealerTypeList' ? { dealerTypeList: list } : { dealerSubTypeList: list })
    if (notFound.length === 0) return apply()

    Swal.fire({
      icon: 'question',
      title: `${notFound.join(', ')} bulunamadı. Yine de yüklensin mi?`,
      showConfirmButton: true,
      allowOutsideClick: false,
      showDenyButton: true,
      confirmButtonText: 'Evet',
      denyButtonText: 'Hayır',
    }).then(() => apply())
  }

  const handleFiles = async (files: File[], type: 'DealerCode' | 'DealerType' | 'DealerSubType') => {
    const dealerCodes: any = await excelToJSON(files[0])
    if (type === 'DealerCode') setDealerCodes(dealerCodes[0], 'dealerTypeOptions', 'XDealerCodeList')
    else if (type === 'DealerType') setDealerCodes(dealerCodes[0], 'dealerTypeOptions', 'XDealerTypeList')
    else if (type === 'DealerSubType') setDealerCodes(dealerCodes[0], 'dealerSubTypeOptions', 'XDealerSubTypeList')
  }

  const handleInputChange = (event: any) => setInputValue(event.target.value)

  const handleAddValue = () => {
    if (inputValue.trim() !== '') {
      const words = inputValue.split(' ')
      const dealerList: { label: string; value: string }[] = []
      words.forEach((word: any) => {
        if (word && !state.dealerCodeList.some((item: any) => item.label === word)) {
          dealerList.push({ label: word, value: word })
        }
      })
      setState({ dealerCodeList: [...state.dealerCodeList, ...dealerList] })
      setInputValue('')
    }
  }

  const handleInputKeyPress = (event: any) => {
    if (event.key === 'Enter') {
      event.preventDefault()
      handleAddValue()
    }
  }

  const handleFileUpload = (e: any) => {
    const f: File | undefined = e.target.files?.[0]
    if (!f) return
    setState({ file: f, fileName: f.name })
  }

  const handleThumbnailUpload = (e: any) => {
    const f: File | undefined = e.target.files?.[0]
    if (!f) return
    if (f.size > 5000000) {
      return Swal.fire('Hata', 'Kapak görseli 5 MB\'den küçük olmalıdır!', 'error')
    }
    setState({ thumbnail: f })
  }

  const save = async () => {
    if (!state.category) return Swal.fire('Hata', 'Kategori seçmelisiniz.', 'error')
    if (!state.title) return Swal.fire('Hata', 'Başlık girmelisiniz.', 'error')

    const sourceType = state.sourceType?.value != null ? Number(state.sourceType.value) : 0

    if (sourceType === 1 && !state.externalUrl) return Swal.fire('Hata', 'Video linki girmelisiniz.', 'error')
    if (sourceType !== 1 && !state.file && !editData?.FileUrl)
      return Swal.fire('Hata', sourceType === 2 ? 'SCORM paketi yüklemelisiniz.' : 'Video dosyası yüklemelisiniz.', 'error')

    props.showLoading()
    const api = new PostVideoLargeApi()
    api
      .save({
        Id: editData?.Id ?? 0,
        VideoCategoryId: Number(state.category.value),
        Title: state.title,
        Description: state.description,
        SourceType: sourceType,
        ExternalUrl: sourceType === 1 ? state.externalUrl : '',
        OrderNo: state.orderNo,
        Active: state.active,
        DealerCodes: state.dealerCodeList.map(o => '0000' + stripSap(String(o.value))),
        DealerTypes: state.dealerTypeList.map(o => String(o.value)),
        DealerSubTypes: state.dealerSubTypeList.map(o => String(o.value)),
        DealerCodesInclude: state.dealerInclude,
        DealerTypesInclude: state.dealerTypeInclude,
        DealerSubTypesInclude: state.dealerSubTypeInclude,
        file: sourceType === 1 ? null : state.file,
        thumbnail: state.thumbnail,
      })
      .then(() => {
        props.hideLoading()
        Swal.fire({
          icon: 'success',
          title: isUpdate ? 'Video güncellendi.' : 'Video eklendi.',
          showConfirmButton: true,
          allowOutsideClick: false,
        }).then(() => navigate('/admin/HomeAndSiteOperation/video/list'))
      })
      .catch((err: any) => {
        props.hideLoading()
        Swal.fire({
          icon: 'error',
          title: err.description ?? 'Yükleme sırasında bir hata oluştu.',
          showConfirmButton: true,
          allowOutsideClick: false,
        })
      })
  }

  return (
    <div className='container-fluid'>
      <div className='content col-lg-12 m-auto'>
        <div className='card'>
          <div className='card-header'>
            <div className='d-flex justify-content-end gap-2'>
              <Link to='/admin/HomeAndSiteOperation/video/list'>
                <IPButton
                  type='button'
                  className='btn btn-sm btn-primary'
                  style={{ width: 'auto', minWidth: 'auto', height: '32px', fontSize: '13px', padding: '0 12px' }}
                  text='Listeye Dön'
                />
              </Link>
              <IPButton
                type='button'
                className='btn btn-sm btn-success'
                style={{ width: 'auto', minWidth: 'auto', height: '32px', fontSize: '13px', padding: '0 12px' }}
                text={isUpdate ? 'Güncelle' : 'Ekle'}
                onClick={() => save()}
              />
            </div>
          </div>
          <div className='card-body'>
            <div className='row mt-5 mb-4'>
              <div className='col-lg-2'>
                <label className='labeltext'>Kategori</label>
                <IPSelectBox
                  id='category'
                  name='category'
                  options={state.categoryOptions}
                  value={state.category}
                  onChangeSingle={option => setState({ category: option })}
                />
              </div>
              <div className='col-lg-1'>
                <label className='labeltext'>Sıra</label>
                <IPInput
                  type='number'
                  id='orderNo'
                  name='orderNo'
                  value={state.orderNo}
                  onChange={e => setState({ orderNo: Number(e.target.value) })}
                />
              </div>
              <div className='col-lg-1'>
                <label className='labeltext'>Aktif</label>
                <div>
                  <IPCheckbox
                    id='active'
                    name='active'
                    checked={state.active}
                    onChange={() => setState({ active: !state.active })}
                  />
                </div>
              </div>
              <div className='col-lg-3'>
                <label className='labeltext'>Başlık</label>
                <IPInput
                  type='text'
                  id='title'
                  name='title'
                  className='form-control'
                  value={state.title}
                  onChange={e => setState({ title: e.target.value })}
                />
              </div>
              <div className='col-lg-5'>
                <div>
                  <label className='labeltext'>Açıklama</label>
                  <textarea
                    className='form-control'
                    id='description'
                    name='description'
                    value={state.description}
                    onChange={e => setState({ description: e.target.value })}
                  />
                </div>
              </div>
            </div>
            <div className='row mt-5 mb-5'>

              <div className='col-lg-6'>
                {/* campaignWrap: reuse the ProductSuggest dealer-section styles; neutralize its page-level look */}
                <div className='campaignWrap' style={{ minHeight: 'auto', background: 'transparent', padding: 0 }}>
                  <div className='leftContainer' style={{ gridTemplateColumns: '170px 1fr', gap: '1.5rem', alignItems: 'flex-start', width: '100%' }}>
                    <div>
                      <a className='campaignExcelBtn' href='/assetWeb/docs/dealer-codes-example.xlsx' target='_blank' rel='noreferrer'>
                        Örnek Excel
                      </a>

                      <div className='listBtnGroup'>
                        <IPButton
                          className={`listCariBtn ${enumFunction(state.selectedDealerList, CariListType.CariCode)}`}
                          text={`Cari Kodu Listesi ${state.dealerCodeList?.length > 0 ? '✅' : ''}`}
                          onClick={() => setState({ selectedDealerList: CariListType.CariCode })}
                        />
                        <IPButton
                          className={`listCariBtn ${enumFunction(state.selectedDealerList, CariListType.CariType)}`}
                          text={`Cari Tipi Listesi ${state.dealerTypeList?.length > 0 ? '✅' : ''}`}
                          onClick={() => setState({ selectedDealerList: CariListType.CariType })}
                        />
                        <IPButton
                          className={`listCariBtn ${enumFunction(state.selectedDealerList, CariListType.CariAltType)}`}
                          text={`Cari Alt Tipi Listesi ${state.dealerSubTypeList?.length > 0 ? '✅' : ''}`}
                          onClick={() => setState({ selectedDealerList: CariListType.CariAltType })}
                        />
                      </div>
                    </div>
                    <div className='p-0 m-0'>
                      <div className={`cariFilterTextAreaWrap ${enumFunction(state.selectedDealerList, CariListType.CariCode)}`}>
                        <div className='topBtnGroup d-flex justify-content-center align-items-center mb-4'>
                          <IPFile
                            id='FileInputDealerCode'
                            multiple={false}
                            handleFiles={e => handleFiles(e, 'DealerCode')}
                            removeAfterSelect={true}
                            className='campaignFileInput'
                            label="Excel'den Yükle"
                          />
                          <IPButton
                            text={'Dahil'}
                            className={`btnIncluded ${state.dealerInclude ? 'active' : ''}`}
                            onClick={() => setState({ dealerInclude: true })}
                          />
                          <IPButton
                            text={'Hariç'}
                            className={`btnExcept ${!state.dealerInclude ? 'active' : ''}`}
                            onClick={() => setState({ dealerInclude: false })}
                          />
                        </div>
                        <CampaignSelect
                          placeholder={'Cari Kod Listesi'}
                          height={180}
                          onChangeMulti={(options: ISelectOption[]) => setState({ dealerCodeList: options })}
                          value={state.dealerCodeList}
                        />
                        <input
                          type='text'
                          placeholder='Cari kodu yazın'
                          value={inputValue}
                          onChange={handleInputChange}
                          onKeyPress={handleInputKeyPress}
                        />
                      </div>

                      <div className={`cariFilterTextAreaWrap ${enumFunction(state.selectedDealerList, CariListType.CariType)}`}>
                        <div className='topBtnGroup d-flex justify-content-center align-items-center mb-4'>
                          <div className='btns'>
                            <IPFile
                              id='FileInputDealerType'
                              multiple={false}
                              handleFiles={e => handleFiles(e, 'DealerType')}
                              removeAfterSelect={true}
                              className='campaignFileInput'
                              label="Excel'den Yükle"
                            />
                          </div>
                          <IPButton
                            text={'Dahil'}
                            className={`btnIncluded ${state.dealerTypeInclude ? 'active' : ''}`}
                            onClick={() => setState({ dealerTypeInclude: true })}
                          />
                          <IPButton
                            text={'Hariç'}
                            className={`btnExcept ${!state.dealerTypeInclude ? 'active' : ''}`}
                            onClick={() => setState({ dealerTypeInclude: false })}
                          />
                        </div>
                        <CampaignSelect
                          placeholder={'Cari Tipi Listesi'}
                          height={180}
                          options={state.dealerTypeOptions}
                          onChangeMulti={(options: ISelectOption[]) => setState({ dealerTypeList: options })}
                          value={state.dealerTypeList}
                        />
                      </div>

                      <div className={`cariFilterTextAreaWrap ${enumFunction(state.selectedDealerList, CariListType.CariAltType)}`}>
                        <div className='topBtnGroup d-flex justify-content-center align-items-center mb-4'>
                          <IPButton
                            text={'Dahil'}
                            className={`btnIncluded ${state.dealerSubTypeInclude ? 'active' : ''}`}
                            onClick={() => setState({ dealerSubTypeInclude: true })}
                          />
                          <IPButton
                            text={'Hariç'}
                            className={`btnExcept ${!state.dealerSubTypeInclude ? 'active' : ''}`}
                            onClick={() => setState({ dealerSubTypeInclude: false })}
                          />
                        </div>
                        <CampaignSelect
                          placeholder={'Cari Alt Tipi Listesi'}
                          height={180}
                          options={state.dealerSubTypeOptions}
                          onChangeMulti={(options: ISelectOption[]) => setState({ dealerSubTypeList: options })}
                          value={state.dealerSubTypeList}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className='col-lg-6'>
                <div className='row mb-3'>
                  <div className='col-lg-6'>
                    <label className='labeltext'>Video Kaynağı</label>
                    <IPSelectBox
                      id='sourceType'
                      name='sourceType'
                      options={sourceTypeOptions}
                      value={state.sourceType}
                      onChangeSingle={option => setState({ sourceType: option })}
                    />
                  </div>
                  {state.sourceType?.value === 1 ? (
                    <div className='col-lg-6'>
                      <label className='labeltext'>Video Linki</label>
                      <IPInput
                        type='text'
                        id='externalUrl'
                        name='externalUrl'
                        className='form-control'
                        placeholder='https://www.youtube.com/watch?v=...'
                        value={state.externalUrl}
                        onChange={e => setState({ externalUrl: e.target.value })}
                      />
                    </div>
                  ) : state.sourceType?.value === 2 ? (
                    <div className='col-lg-6'>
                      <label className='labeltext'>SCORM Paketi (.zip)</label>
                      <input type='file' accept='.zip,application/zip,application/x-zip-compressed' onChange={e => handleFileUpload(e)} />
                      {state.fileName ? <p>Seçilen: {state.fileName}</p> : editData?.FileUrl && <p>Mevcut paket: {editData.FileUrl}</p>}
                    </div>
                  ) : (
                    <div className='col-lg-6'>
                      <label className='labeltext'>Video Dosyası</label>
                      <input type='file' accept='video/*' onChange={e => handleFileUpload(e)} />
                      {state.fileName ? <p>Seçilen: {state.fileName}</p> : editData?.FileUrl && <p>Mevcut dosya: {editData.FileUrl}</p>}
                    </div>
                  )}
                </div>
                <div className='row'>
                  <div className='col-lg-6'>
                    <label className='labeltext'>Kapak Görseli (opsiyonel)</label>
                    <input type='file' accept='image/*' onChange={e => handleThumbnailUpload(e)} />
                    {editData?.ThumbnailUrl && !state.thumbnail && <p>Mevcut görsel: {editData.ThumbnailUrl}</p>}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
export default componentWithContext(AddVideo)
